// src/pages/CancerCenterDetailPage.jsx
// Detailed Profile for every NCI-Designated Cancer Center in the US.

import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { NCI_CANCER_CENTERS } from '../data/cancerCentersDirectoryData';

export default function CancerCenterDetailPage() {
  const { centerSlug } = useParams();

  const center = NCI_CANCER_CENTERS.find((c) => c.slug === centerSlug);

  if (!center) {
    return <Navigate to="/find-cancer-centers" replace />;
  }

  const cleanPhone = center.phone ? center.phone.replace(/[^0-9]/g, '') : '';

  // Structured Data Schema
  const jsonLdHospital = {
    '@context': 'https://schema.org',
    '@type': 'Hospital',
    name: center.name,
    url: `https://www.getbyonco.com/find-cancer-centers/${center.slug}`,
    telephone: center.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: center.address,
      addressLocality: center.city,
      addressRegion: center.stateAbbr,
      postalCode: center.zip,
      addressCountry: 'US',
    },
    medicalSpecialty: center.keySpecialties || ['Oncology', 'Hematology', 'Surgical Oncology'],
    isAcceptingNewPatients: true,
  };

  const jsonLdWebPage = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: `${center.name} - NCI Cancer Center Profile | ByOnco`,
    description: `Official profile for ${center.name} in ${center.city}, ${center.state}. Address: ${center.address}, phone: ${center.phone}, clinical trials, key specialties, and virtual second opinion options.`,
    url: `https://www.getbyonco.com/find-cancer-centers/${center.slug}`,
    publisher: {
      '@type': 'Organization',
      name: 'ByOnco',
      url: 'https://www.getbyonco.com',
    },
  };

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.getbyonco.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Find Cancer Centers',
        item: 'https://www.getbyonco.com/find-cancer-centers',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: center.name,
        item: `https://www.getbyonco.com/find-cancer-centers/${center.slug}`,
      },
    ],
  };

  return (
    <div className="bg-slate-50 min-h-screen text-slate-900 pt-24 pb-20">
      <Helmet>
        <title>{`${center.name} | NCI Cancer Center Appointments &amp; Trials | ByOnco`}</title>
        <meta
          name="description"
          content={`Complete clinical guide for ${center.name} in ${center.city}, ${center.state}. Address: ${center.address}, Phone: ${center.phone}. ${center.clinicalTrialsCount}, key oncology specialties, and second opinion intake.`}
        />
        <link rel="canonical" href={`https://www.getbyonco.com/find-cancer-centers/${center.slug}`} />
        <script type="application/ld+json">{JSON.stringify(jsonLdHospital)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdWebPage)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>
      </Helmet>

      {/* Breadcrumb Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <nav className="flex text-xs text-slate-500 gap-2 items-center flex-wrap">
          <Link to="/" className="hover:text-emerald-700">Home</Link>
          <span>/</span>
          <Link to="/find-cancer-centers" className="hover:text-emerald-700">Cancer Centers</Link>
          <span>/</span>
          <Link to={`/cancer-centers/${center.stateSlug}`} className="hover:text-emerald-700">{center.state}</Link>
          <span>/</span>
          <span className="text-slate-800 font-semibold truncate">{center.shortName}</span>
        </nav>
      </div>

      {/* Hero Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="bg-white rounded-3xl p-6 sm:p-10 border border-slate-200/90 shadow-sm">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
              {center.designation}
            </span>
            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-slate-100 text-slate-700">
              {center.city}, {center.state} ({center.stateAbbr})
            </span>
            {center.hasProton && (
              <span className="text-xs font-semibold px-3 py-1 rounded-full bg-sky-100 text-sky-800 border border-sky-200">
                ⚡ NAPT Proton Center
              </span>
            )}
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-snug mb-4">
            {center.name}
          </h1>

          <p className="text-slate-600 text-base sm:text-lg max-w-4xl leading-relaxed mb-6">
            {center.description}
          </p>

          <div className="flex flex-wrap gap-4 items-center">
            {cleanPhone && (
              <a
                href={`tel:${cleanPhone}`}
                className="px-6 py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 transition shadow-sm inline-flex items-center gap-2"
              >
                <span>Call Appointment Desk:</span>
                <span>{center.phone}</span>
              </a>
            )}
            <Link
              to="/cancer-second-opinion"
              className="px-6 py-3 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-800 transition"
            >
              Get Virtual Second Opinion ($199)
            </Link>
            {center.website && (
              <a
                href={center.website}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-3 rounded-xl border border-slate-300 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition"
              >
                Official Website ↗
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left 2 Columns: Clinical Strengths & Guide */}
          <div className="lg:col-span-2 space-y-8">
            {/* Key Clinical Specialties */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 mb-4">
                Clinical Programs &amp; Subspecialties
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(center.keySpecialties || []).map((spec, idx) => (
                  <div
                    key={idx}
                    className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-3"
                  >
                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    <span className="text-sm font-semibold text-slate-800">{spec}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Advanced Technologies Available */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 mb-4">
                Advanced Treatment Technologies
              </h2>
              <div className="flex flex-wrap gap-2">
                {(center.technologies || []).map((tech, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {/* Patient Intake & Second Opinion Protocol */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 mb-3">
                How to Transfer Records &amp; Get an Evaluation at {center.shortName}
              </h2>
              <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                Visiting an NCI-designated comprehensive cancer center requires organized medical records to ensure multidisciplinary tumor board review can proceed without delay.
              </p>

              <ol className="space-y-4 text-sm text-slate-700">
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold shrink-0">1</span>
                  <div>
                    <strong className="block text-slate-900">Request Original Pathology Slides:</strong>
                    Ask your diagnostic hospital pathology laboratory for unstained histology slides or formalin-fixed paraffin-embedded (FFPE) blocks for second opinion re-review.
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold shrink-0">2</span>
                  <div>
                    <strong className="block text-slate-900">Acquire High-Resolution DICOM Imaging:</strong>
                    Obtain raw DICOM files on CD or via secure electronic image sharing (Ambra/PowerShare) for all contrast CT, PET/CT, and MRI scans.
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold shrink-0">3</span>
                  <div>
                    <strong className="block text-slate-900">Gather Molecular &amp; NGS Next-Generation Sequencing:</strong>
                    Compile FoundationOne, Guardant360, or institutional genomic panels to assess actionable alterations (e.g. EGFR, ALK, KRAS, BRAF, BRCA, HER2).
                  </div>
                </li>
              </ol>
            </div>

            {/* ByOnco Virtual Option CTA */}
            <div className="bg-emerald-950 text-white rounded-2xl p-6 sm:p-8 border border-emerald-800">
              <div className="inline-block text-xs font-bold tracking-widest text-emerald-400 uppercase mb-2">
                Save Travel Costs &amp; Time
              </div>
              <h3 className="text-xl font-bold mb-3">
                Can't Travel to {center.city}? Get a Virtual Second Opinion
              </h3>
              <p className="text-sm text-emerald-100/90 leading-relaxed mb-6">
                Traveling across state lines involves thousands of dollars in flights, hotels, and lost wages. ByOnco connects you with US board-certified oncologists for independent review of your scans and molecular pathology report for $199 within 48–72 hours.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/cancer-second-opinion"
                  className="px-5 py-2.5 rounded-xl bg-emerald-500 text-slate-950 font-bold text-xs hover:bg-emerald-400 transition"
                >
                  Start Virtual Second Opinion ($199)
                </Link>
                <Link
                  to="/cancer-teleconsultation"
                  className="px-5 py-2.5 rounded-xl bg-white/10 text-white font-semibold text-xs hover:bg-white/20 transition"
                >
                  Schedule Teleconsultation
                </Link>
              </div>
            </div>
          </div>

          {/* Right Column: Center Quick Facts Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <h3 className="font-bold text-slate-900 text-base mb-4 pb-3 border-b border-slate-100">
                Center Quick Facts
              </h3>

              <div className="space-y-3.5 text-xs">
                <div>
                  <span className="text-slate-500 block">Official Name</span>
                  <span className="font-semibold text-slate-900">{center.name}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">NCI Designation</span>
                  <span className="font-semibold text-emerald-700">{center.designation}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Practice Address</span>
                  <span className="font-semibold text-slate-900">
                    {center.address}, {center.city}, {center.stateAbbr} {center.zip}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Main Phone</span>
                  <a href={`tel:${cleanPhone}`} className="font-semibold text-emerald-700 hover:underline">
                    {center.phone}
                  </a>
                </div>
                <div>
                  <span className="text-slate-500 block">Clinical Trials Volume</span>
                  <span className="font-semibold text-slate-900">{center.clinicalTrialsCount}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">State Oncology Directory</span>
                  <Link
                    to={`/cancer-centers/${center.stateSlug}`}
                    className="font-semibold text-emerald-700 hover:underline"
                  >
                    View all {center.state} Cancer Centers &rarr;
                  </Link>
                </div>
                <div>
                  <span className="text-slate-500 block">Find Oncologists in {center.state}</span>
                  <Link
                    to={`/find-oncologists/${center.stateSlug}`}
                    className="font-semibold text-emerald-700 hover:underline"
                  >
                    Browse {center.state} Oncologists &rarr;
                  </Link>
                </div>
              </div>
            </div>

            {/* Other NCI Centers in State */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <h4 className="font-bold text-slate-900 text-sm mb-3">
                Explore More Cancer Centers
              </h4>
              <div className="space-y-2 text-xs">
                {NCI_CANCER_CENTERS.filter((c) => c.slug !== center.slug).slice(0, 5).map((c) => (
                  <Link
                    key={c.slug}
                    to={`/find-cancer-centers/${c.slug}`}
                    className="block p-2 rounded-lg hover:bg-slate-50 transition border border-transparent hover:border-slate-200 text-slate-700 font-medium"
                  >
                    <div className="font-semibold text-slate-900">{c.shortName}</div>
                    <div className="text-[11px] text-slate-500">{c.city}, {c.stateAbbr}</div>
                  </Link>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100">
                <Link
                  to="/find-cancer-centers"
                  className="text-xs font-bold text-emerald-700 hover:text-emerald-900"
                >
                  View All 72 NCI Centers &rarr;
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
